// +----------------------------------------------------------------------
// | useDecisionPoll：AI 分析中轮询决策状态
// | 用法：当决策 next === 'analyzing' 时打开 enabled，拿到最新数据后自动停
// +----------------------------------------------------------------------
import { useCallback, useEffect, useRef, useState } from 'react';
import { getDecision } from '../api/decision';

const STILL_RUNNING = ['analyzing', 'pending'];

export default function useDecisionPoll(decisionId, options = {}) {
  const { enabled = false, interval = 1500, timeout = 60000, onUpdate } = options;

  const [polling, setPolling] = useState(false);
  const [error, setError] = useState('');
  const timerRef = useRef(null);
  const startedAtRef = useRef(0);
  const onUpdateRef = useRef(onUpdate);

  onUpdateRef.current = onUpdate;

  const stop = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setPolling(false);
  }, []);

  useEffect(() => {
    if (!enabled || !decisionId) {
      stop();
      return undefined;
    }

    let cancelled = false;
    startedAtRef.current = Date.now();
    setPolling(true);
    setError('');

    const tick = async () => {
      if (cancelled) return;
      try {
        const data = await getDecision(decisionId);
        if (cancelled) return;
        if (onUpdateRef.current) onUpdateRef.current(data);

        const next = data && data.next;
        const finished = !next || !STILL_RUNNING.includes(next);
        const overtime = Date.now() - startedAtRef.current > timeout;

        if (finished || overtime) {
          stop();
          return;
        }
      } catch (e) {
        if (cancelled) return;
        setError(e.message || '获取进度失败');
        stop();
        return;
      }

      timerRef.current = setTimeout(tick, interval);
    };

    timerRef.current = setTimeout(tick, interval);

    return () => {
      cancelled = true;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [enabled, decisionId, interval, timeout, stop]);

  return { polling, error, stop };
}
